import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import LeadPhoneCall, {leadPhoneCallTarget} from '@adv-mt/mt-lead-phone-call'
import IconCheck from '@adv-mt/mt-svg-icons/lib/Check'
import IconContacts from '@adv-mt/mt-svg-icons/lib/Contacts'

import {CardAdPricePriceDrop} from './card-related-ad-price-drop.js'

const CONTACT_TYPES = {
  none: 'none',
  email: 'email',
  phoneCall: 'phoneCall'
}

const BUTTON_COLOR = {
  ACCENT: 'accent',
  NEUTRAL: 'neutral',
  PRIMARY: 'primary',
  SUCCESS: 'success'
}

const BUTTON_DESIGN = {
  FLAT: atomButtonDesigns.FLAT,
  OUTLINE: atomButtonDesigns.OUTLINE,
  SOLID: atomButtonDesigns.SOLID
}

const CardRelatedAdContent = ({
  ad,
  contactType = CONTACT_TYPES.none,
  extendedTrackingData,
  hasCriteoEvents = false,
  leadTarget = leadPhoneCallTarget.relatedVehicle,
  onContact
}) => {
  const {domain, i18n} = useContext(Context)
  const [leadData, setLeadData] = useState()

  const hasContactEmail = contactType === CONTACT_TYPES.email
  const hasContactPhoneCall = contactType === CONTACT_TYPES.phoneCall && !ad?.hidePhone
  const hasContact = contactType !== CONTACT_TYPES.none

  useMount(async () => {
    if (hasContact) {
      const [, _leadData] = await domain
        .get('get_lead_data_tracking_use_case')
        .execute({ad, dealerId: ad?.seller?.contractId})
      setLeadData(_leadData)
    }
  })

  const {
    contractId,
    id: adId,
    isAlreadyContacted = false,
    km,
    monthlySubscriptionFee,
    offerType,
    phone,
    price,
    year
  } = ad

  const hasYear = Boolean(year)
  const hasKm = Boolean(km)
  const hasPrice = Boolean(price)

  const {OFFER_TYPES} = domain.get('config').get('vehicleSpecifications')
  const offerTypeLabel = offerType?.id != null && OFFER_TYPES[offerType.id] && i18n.t(OFFER_TYPES[offerType.id].name)
  const hasOfferType = Boolean(offerTypeLabel)

  const contactedButtonLiteral = i18n.t(`COMMON.MODAL_RELATED_ADS_ALREADY_CONTACTED_BUTTON`)
  const contactButtonLiteral = i18n.t(`COMMON.MODAL_RELATED_ADS_CONTACT_BUTTON`)

  const handleContact = e => {
    e.preventDefault()
    e.stopPropagation()
    onContact({...ad, leadData, extendedTrackingData})
  }

  const getLeftIcon = () => {
    if (isAlreadyContacted) return <IconCheck size="medium" />
    if (hasContactEmail) return <IconContacts />
  }

  const getDefaultContactButton = () => {
    if (!isAlreadyContacted) return BUTTON_COLOR.ACCENT
    return BUTTON_COLOR.NEUTRAL
  }

  const getPhoneButtonColor = () => {
    return isAlreadyContacted ? BUTTON_COLOR.NEUTRAL : BUTTON_COLOR.ACCENT
  }

  const handlePhoneCall = () => onContact({...ad})

  const buttonProps = {
    color: getDefaultContactButton(),
    design: BUTTON_DESIGN.FLAT,
    disabled: isAlreadyContacted,
    leftIcon: getLeftIcon(),
    onClick: handleContact,
    fullWidth: true,
    shape: atomButtonShapes.CIRCULAR
  }

  const mailContactbutton = hasContactEmail && (
    <div className="mt-CardRelatedAd-cta">
      <AtomButton {...buttonProps}>{isAlreadyContacted ? contactedButtonLiteral : contactButtonLiteral}</AtomButton>
    </div>
  )

  const phoneContactButton = hasContactPhoneCall && (
    <div className="mt-CardRelatedAd-cta">
      <LeadPhoneCall
        adId={adId}
        color={getPhoneButtonColor()}
        contractId={contractId}
        design={BUTTON_DESIGN.FLAT}
        extendedLeadTrackingData={{...leadData, ...extendedTrackingData}}
        hasCriteoEvents={hasCriteoEvents}
        isAlreadyContacted={isAlreadyContacted}
        isFullWidth
        onLeadPhoneCall={handlePhoneCall}
        phone={phone}
        target={leadTarget}
      />
    </div>
  )

  const contactButtons = hasContact && (
    <div className="mt-CardRelatedAd-cta">
      {mailContactbutton}
      {phoneContactButton}
    </div>
  )

  return (
    <>
      {hasOfferType && (
        <AtomBadge
          className="mt-CardRelatedAd-offerType"
          design={atomBadgeDesigns.SOFT}
          label={offerTypeLabel}
          size={atomBadgeSizes.MEDIUM}
          type={atomBadgeTypes.NEUTRAL}
        />
      )}
      <div className="mt-CardRelatedAd-price">
        {hasPrice && <span className="mt-CardRelatedAd-priceAmount">{i18n.c(price)}</span>}
        {monthlySubscriptionFee ? (
          <>
            <span className="mt-CardRelatedAd-priceLabel">{i18n.t('COMMON_FROM')}</span>
            <div className="mt-CardRelatedAd-priceAmount">
              {i18n.c(monthlySubscriptionFee)}
              <span className="mt-CardRelatedAd-priceAmountSub">{`/${i18n
                .t('COMMON.PLURALS.MONTH', 1)
                .toLowerCase()}`}</span>
            </div>
          </>
        ) : null}
        {ad.priceDrop && !monthlySubscriptionFee ? <CardAdPricePriceDrop priceDropData={ad.priceDrop} /> : null}
      </div>
      <p className="mt-CardRelatedAd-title">{ad.title}</p>
      {(hasKm || hasYear) && (
        <p className="mt-CardRelatedAd-info">
          {hasKm && <span className="mt-CardRelatedAd-infoKm">{i18n.n(km)} km</span>}
          {hasKm && hasYear && <span className="mt-CardRelatedAd-infoDivider" />}
          {hasYear && <span className="mt-CardRelatedAd-infoYear">{year}</span>}
        </p>
      )}
      {contactButtons}
    </>
  )
}

CardRelatedAdContent.displayName = 'CardRelatedAdContent'

CardRelatedAdContent.propTypes = {
  ad: PropTypes.object.isRequired,
  contactType: PropTypes.oneOf(Object.values(CONTACT_TYPES)),
  extendedTrackingData: PropTypes.object,
  hasCriteoEvents: PropTypes.bool,
  leadTarget: PropTypes.oneOf(Object.values(leadPhoneCallTarget)),
  onContact: PropTypes.func
}

export {CONTACT_TYPES as cardRelatedAdContactTypes, leadPhoneCallTarget as cardRelatedAdLeadTargets}
export default CardRelatedAdContent
